import type { useClientTranslation } from '@/lib/core/i18n';
import type { CartShopDto } from '@/services/platform/api/model';

type Translate = ReturnType<typeof useClientTranslation>;

// The generated client types every non-2xx cart and checkout response body as `data: void` — an
// orval/spec gap. At runtime the API actually returns `{ error: string }` for these (e.g. 409
// insufficient_stock, 409 prices_changed), customFetch just doesn't know how to type it.
//
// Lives here rather than in `useCartStore` because both the cart store and the checkout data layer
// read these bodies, and the latter has no business importing from a zustand store.
export function extractErrorCode(data: unknown): string | undefined {
  return typeof data === 'object' && data !== null && 'error' in data
    ? String((data as { error?: unknown }).error)
    : undefined;
}

// The `prices_changed` 409 body carries the repriced cart alongside the error code
// (`{ error, cart }`) — same "mistyped as void" situation as extractErrorCode. Returned by
// `POST /me/checkout/payment-intents` when CPI re-priced the basket between the bag and the pay
// button.
export function extractRepricedCart(data: unknown): CartShopDto | undefined {
  return typeof data === 'object' && data !== null && 'cart' in data
    ? ((data as { cart?: CartShopDto }).cart ?? undefined)
    : undefined;
}

/**
 * Maps a cart API error code (see `CartErrors` on the backend) to a translated,
 * user-facing message. Unknown/missing codes fall back to a generic message so
 * errors are never silently swallowed.
 */
export function mapCartErrorMessage(t: Translate, errorCode?: string): string {
  switch (errorCode) {
    case 'insufficient_stock':
      return t('App.Shop.InsufficientStockError');
    default:
      return t('App.Shop.GenericCartError');
  }
}
